import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "d-flex align-center gap-3" };
const _hoisted_2 = { class: "text-body-2" };
const _hoisted_3 = { class: "mb-4" };
const _hoisted_4 = { class: "d-flex align-center" };
const _hoisted_5 = ["onClick"];
const _hoisted_6 = { class: "d-flex align-center justify-end" };
const _hoisted_7 = { class: "font-weight-medium" };
const _hoisted_8 = { class: "formulary-details" };
const _hoisted_9 = {
    key: 0,
    class: "mb-4"
};
const _hoisted_10 = { class: "text-body-2" };
const _hoisted_11 = { key: 1 };
const _hoisted_12 = {
    class: "text-body-2",
    style: { "white-space": "pre-wrap" }
};
import { ref, computed, onMounted, watch } from "vue";
import { camelizeKeys } from "humps";
import { intakeService } from "@/services/intakeService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ReimbursementDataCard',
    props: {
        order: {}
    },
    setup(__props) {
        const props = __props;
        const loading = ref(false);
        const loadingOptions = ref(false);
        const reimbursementData = ref(null);
        const availableFields = ref([]);
        const errorMessage = ref(null);
        const payorName = ref(null);
        const productType = ref(null);
        const selectedPayorName = ref(null);
        const selectedProductType = ref(null);
        const payorNames = ref([]);
        const productTypes = ref([]);
        const productNameTypes = ref({});
        const selectedColumns = ref([]);
        const showColumnSelector = ref(false);
        const sortColumn = ref(null);
        const sortDirection = ref("asc");
        const formularyDetails = ref(null);
        // Default columns to display
        const defaultColumns = ["Reimb", "Detail", "% +/-", "GP$", "GP% w/ Discounts"];
        const shouldShow = computed(() => {
            return props.order && props.order.id;
        });
        const payorNameSource = computed(() => {
            return props.order?.orderForm?.extraData?.reimbursementPayorName || props.order?.insuranceCard?.payorName;
        });
        const hasDataToDisplay = computed(() => {
            return (reimbursementData.value &&
                Array.isArray(reimbursementData.value) &&
                reimbursementData.value.length > 0 &&
                availableFields.value &&
                availableFields.value.length > 0);
        });
        // Get visible columns (selected columns that are available)
        const visibleColumns = computed(() => {
            if (!availableFields.value || availableFields.value.length === 0) {
                return [];
            }
            return selectedColumns.value.filter((col) => availableFields.value.includes(col));
        });
        // Get raw numeric value for sorting
        const getRawValue = (row, field) => {
            const camelField = getCamelCaseField(field);
            const value = row[camelField];
            if (value === null || value === undefined || value === "") {
                return null;
            }
            // Handle parentheses format (negative values)
            if (typeof value === "string") {
                const trimmed = value.trim();
                if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
                    const numStr = trimmed.slice(1, -1).trim();
                    return -Math.abs(parseFloat(numStr));
                }
                // Try to parse as number
                const num = parseFloat(value);
                if (!isNaN(num)) {
                    return num;
                }
                // Return as string for text sorting
                return value.toLowerCase();
            }
            if (typeof value === "number") {
                return value;
            }
            return String(value).toLowerCase();
        };
        // Sorted data based on current sort column and direction
        const sortedData = computed(() => {
            if (!reimbursementData.value || reimbursementData.value.length === 0) {
                return [];
            }
            if (!sortColumn.value) {
                return reimbursementData.value;
            }
            const data = [...reimbursementData.value];
            const field = sortColumn.value;
            return data.sort((a, b) => {
                let aVal = getRawValue(a, field);
                let bVal = getRawValue(b, field);
                // Handle null/undefined values
                if (aVal === null || aVal === undefined)
                    return 1;
                if (bVal === null || bVal === undefined)
                    return -1;
                // Compare values
                let comparison;
                if (typeof aVal === "number" && typeof bVal === "number") {
                    comparison = aVal - bVal;
                }
                else {
                    comparison = String(aVal).localeCompare(String(bVal));
                }
                return sortDirection.value === "asc" ? comparison : -comparison;
            });
        });
        // Convert a field name to camelCase to match the data keys (API interceptor converts them)
        const getCamelCaseField = (field) => {
            const camelized = camelizeKeys({ [field]: "" });
            return Object.keys(camelized)[0];
        };
        // Fields that should be formatted as currency
        const currencyFields = ["Reimb", "GP$"];
        // Check if a value is negative (for styling)
        const isNegativeValue = (value) => {
            if (value === null || value === undefined || value === "") {
                return false;
            }
            // Check if it's a string with parentheses (negative value format)
            if (typeof value === "string" && value.trim().startsWith("(") && value.trim().endsWith(")")) {
                return true;
            }
            // Check if it's a negative number
            const num = typeof value === "number" ? value : parseFloat(String(value));
            if (!isNaN(num) && typeof num === "number") {
                return num < 0;
            }
            return false;
        };
        const formatValue = (value, fieldName) => {
            if (value === null || value === undefined || value === "") {
                return "-";
            }
            // Format percentage values
            if (typeof value === "string" && value.includes("%")) {
                return value;
            }
            // Handle values in parentheses format (negative values)
            let num;
            if (typeof value === "string") {
                const trimmed = value.trim();
                if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
                    // Extract the number and make it negative
                    const numStr = trimmed.slice(1, -1).trim();
                    num = -Math.abs(parseFloat(numStr));
                }
                else {
                    num = parseFloat(value);
                }
            }
            else if (typeof value === "number") {
                num = value;
            }
            else {
                return String(value);
            }
            if (!isNaN(num)) {
                // Format as currency if it's a currency field
                if (currencyFields.includes(fieldName)) {
                    return new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(num);
                }
                // Format other numeric values with 2 decimal places
                return num.toFixed(2);
            }
            return String(value);
        };
        const fetchReimbursementOptions = async () => {
            loadingOptions.value = true;
            try {
                const response = await intakeService.getReimbursementOptions();
                if (response.payorNames) {
                    payorNames.value = response.payorNames;
                }
                if (response.productTypes) {
                    productTypes.value = response.productTypes;
                }
                if (response.productNameTypes) {
                    productNameTypes.value = response.productNameTypes;
                }
                await setSelectedReimbursementFields();
            }
            catch (error) {
                // Error fetching reimbursement options
            }
            finally {
                loadingOptions.value = false;
            }
        };
        const setSelectedReimbursementFields = async () => {
            selectedPayorName.value = payorNameSource.value ?? null;
            const therapyAdministration = props.order?.orderForm?.therapyAdministration;
            if (therapyAdministration && therapyAdministration.length > 0) {
                const drugName = therapyAdministration[0]?.name;
                if (drugName) {
                    const matchingKey = Object.keys(productNameTypes.value).find((key) => key.toLowerCase() === getCamelCaseField(drugName).toLowerCase());
                    let matchingType = matchingKey ? productNameTypes.value[matchingKey] : null;
                    if (!matchingType) {
                        if (productTypes.value.includes(drugName)) {
                            matchingType = drugName;
                        }
                    }
                    if (matchingType) {
                        selectedProductType.value = matchingType;
                    }
                }
            }
            // Fetch data if we have initial values
            if (selectedPayorName.value && selectedProductType.value) {
                await fetchReimbursementData();
            }
        };
        const fetchReimbursementData = async () => {
            if (!props.order?.id || !selectedPayorName.value || !selectedProductType.value) {
                return;
            }
            loading.value = true;
            errorMessage.value = null;
            reimbursementData.value = null;
            availableFields.value = [];
            try {
                const response = await intakeService.getReimbursementData(props.order.id, selectedPayorName.value, selectedProductType.value);
                payorName.value = response.payorName;
                productType.value = response.productType;
                const formularyData = response.formularyDetails;
                if (formularyData) {
                    formularyDetails.value = {
                        preferredDrugs: formularyData.preferredDrugs || null,
                        comments: formularyData.comments || null,
                    };
                }
                else {
                    formularyDetails.value = null;
                }
                if (response.message) {
                    errorMessage.value = response.message;
                    reimbursementData.value = null;
                    availableFields.value = [];
                }
                else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    reimbursementData.value = response.data;
                    availableFields.value = response.fields || [];
                    errorMessage.value = null;
                    // Reset sorting when new data is loaded
                    sortColumn.value = null;
                    sortDirection.value = "asc";
                    // Initialize selected columns with defaults (only if not already set)
                    if (selectedColumns.value.length === 0) {
                        selectedColumns.value = defaultColumns.filter((col) => availableFields.value.includes(col));
                        // If no default columns are available, select all
                        if (selectedColumns.value.length === 0) {
                            selectedColumns.value = [...availableFields.value];
                        }
                    }
                    else {
                        // Update selected columns to only include available fields
                        selectedColumns.value = selectedColumns.value.filter((col) => availableFields.value.includes(col));
                        // If no selected columns are available, use defaults or all
                        if (selectedColumns.value.length === 0) {
                            selectedColumns.value = defaultColumns.filter((col) => availableFields.value.includes(col));
                            if (selectedColumns.value.length === 0) {
                                selectedColumns.value = [...availableFields.value];
                            }
                        }
                    }
                }
                else {
                    errorMessage.value = "No reimbursement data available.";
                    reimbursementData.value = null;
                    availableFields.value = [];
                }
            }
            catch (error) {
                const err = error;
                errorMessage.value = err?.response?.data?.message || err.message || "Failed to load reimbursement data.";
            }
            finally {
                loading.value = false;
            }
        };
        const onSelectionChange = () => {
            if (selectedPayorName.value && selectedProductType.value) {
                fetchReimbursementData();
            }
            else {
                reimbursementData.value = null;
                availableFields.value = [];
                errorMessage.value = null;
            }
        };
        const sortByColumn = (column) => {
            if (sortColumn.value === column) {
                // Toggle direction if clicking the same column
                sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
            }
            else {
                // Set new column and default to ascending
                sortColumn.value = column;
                sortDirection.value = "asc";
            }
        };
        // Watch for changes on payor name
        watch(payorNameSource, () => {
            setSelectedReimbursementFields();
        }, { immediate: true });
        // Watch for changes
        watch(() => props.order?.orderForm?.therapyAdministration, () => {
            setSelectedReimbursementFields();
        }, { immediate: true });
        onMounted(() => {
            fetchReimbursementOptions();
        });
        return (_ctx, _cache) => {
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_col = _resolveComponent("v-col");
            const _component_v_row = _resolveComponent("v-row");
            const _component_v_card_text = _resolveComponent("v-card-text");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_checkbox = _resolveComponent("v-checkbox");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_expand_transition = _resolveComponent("v-expand-transition");
            const _component_v_table = _resolveComponent("v-table");
            const _component_v_expansion_panel_title = _resolveComponent("v-expansion-panel-title");
            const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text");
            const _component_v_expansion_panel = _resolveComponent("v-expansion-panel");
            const _component_v_expansion_panels = _resolveComponent("v-expansion-panels");
            return (shouldShow.value)
                ? (_openBlock(), _createBlock(_component_v_card, {
                    key: 0,
                    class: "mt-4 mb-4",
                    color: "surface",
                    variant: "elevated",
                    elevation: "0"
                }, {
                    default: _withCtx(() => [
                        _createVNode(_component_v_card_title, null, {
                            default: _withCtx(() => _cache[5] || (_cache[5] = [
                                _createElementVNode("h2", { class: "text-h6" }, "Reimbursement Information", -1)
                            ])),
                            _: 1,
                            __: [5]
                        }),
                        _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_row, null, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_col, {
                                            cols: "12",
                                            md: "6"
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_select, {
                                                    modelValue: selectedPayorName.value,
                                                    "onUpdate:modelValue": [
                                                        _cache[0] || (_cache[0] = ($event) => ((selectedPayorName).value = $event)),
                                                        onSelectionChange
                                                    ],
                                                    items: payorNames.value,
                                                    label: "Payor",
                                                    variant: "outlined",
                                                    density: "compact",
                                                    clearable: ""
                                                }, null, 8, ["modelValue", "items"])
                                            ]),
                                            _: 1
                                        }),
                                        _createVNode(_component_v_col, {
                                            cols: "12",
                                            md: "6"
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_select, {
                                                    modelValue: selectedProductType.value,
                                                    "onUpdate:modelValue": [
                                                        _cache[1] || (_cache[1] = ($event) => ((selectedProductType).value = $event)),
                                                        onSelectionChange
                                                    ],
                                                    items: productTypes.value,
                                                    label: "Product Type",
                                                    variant: "outlined",
                                                    density: "compact",
                                                    clearable: ""
                                                }, null, 8, ["modelValue", "items"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                })
                            ]),
                            _: 1
                        }),
                        (loading.value)
                            ? (_openBlock(), _createBlock(_component_v_card_text, { key: 0 }, {
                                default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_1, [
                                        _createVNode(_component_v_progress_circular, {
                                            indeterminate: "",
                                            color: "primary",
                                            size: "24"
                                        }),
                                        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-body-1" }, "Loading reimbursement data...", -1))
                                    ])
                                ]),
                                _: 1
                            }))
                            : (errorMessage.value)
                                ? (_openBlock(), _createBlock(_component_v_card_text, { key: 1 }, {
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_alert, {
                                            type: "warning",
                                            variant: "outlined",
                                            class: "mb-0"
                                        }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_2, _toDisplayString(errorMessage.value), 1)
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }))
                                : (!reimbursementData.value || reimbursementData.value.length === 0)
                                    ? (_openBlock(), _createBlock(_component_v_card_text, { key: 2 }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_alert, {
                                                type: "info",
                                                variant: "outlined",
                                                class: "mb-0"
                                            }, {
                                                default: _withCtx(() => _cache[7] || (_cache[7] = [
                                                    _createElementVNode("div", { class: "text-body-2" }, "No reimbursement data available for this payor and product combination.", -1)
                                                ])),
                                                _: 1,
                                                __: [7]
                                            })
                                        ]),
                                        _: 1
                                    }))
                                    : (hasDataToDisplay.value)
                                        ? (_openBlock(), _createBlock(_component_v_card_text, { key: 3 }, {
                                            default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_3, [
                                                    _createVNode(_component_v_btn, {
                                                        variant: "outlined",
                                                        size: "small",
                                                        onClick: _cache[2] || (_cache[2] = ($event) => (showColumnSelector.value = !showColumnSelector.value))
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createVNode(_component_v_icon, {
                                                                icon: "mdi-view-column",
                                                                class: "mr-2"
                                                            }),
                                                            _cache[8] || (_cache[8] = _createTextVNode(" Select Columns ", -1))
                                                        ]),
                                                        _: 1,
                                                        __: [8]
                                                    }),
                                                    _createVNode(_component_v_expand_transition, null, {
                                                        default: _withCtx(() => [
                                                            (showColumnSelector.value)
                                                                ? (_openBlock(), _createBlock(_component_v_card, {
                                                                    key: 0,
                                                                    class: "mt-2",
                                                                    variant: "outlined"
                                                                }, {
                                                                    default: _withCtx(() => [
                                                                        _createVNode(_component_v_card_text, null, {
                                                                            default: _withCtx(() => [
                                                                                _cache[9] || (_cache[9] = _createElementVNode("div", { class: "text-body-2 font-weight-medium mb-2" }, "Select columns to display:", -1)),
                                                                                _createVNode(_component_v_row, null, {
                                                                                    default: _withCtx(() => [
                                                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableFields.value, (field) => {
                                                                                            return (_openBlock(), _createBlock(_component_v_col, {
                                                                                                key: field,
                                                                                                cols: "12",
                                                                                                sm: "6",
                                                                                                md: "4"
                                                                                            }, {
                                                                                                default: _withCtx(() => [
                                                                                                    _createVNode(_component_v_checkbox, {
                                                                                                        modelValue: selectedColumns.value,
                                                                                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((selectedColumns).value = $event)),
                                                                                                        value: field,
                                                                                                        label: field,
                                                                                                        density: "compact",
                                                                                                        "hide-details": ""
                                                                                                    }, null, 8, ["modelValue", "value", "label"])
                                                                                                ]),
                                                                                                _: 2
                                                                                            }, 1024));
                                                                                        }), 128))
                                                                                    ]),
                                                                                    _: 1
                                                                                })
                                                                            ]),
                                                                            _: 1,
                                                                            __: [9]
                                                                        })
                                                                    ]),
                                                                    _: 1
                                                                }))
                                                                : _createCommentVNode("", true)
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _createVNode(_component_v_table, { density: "compact" }, {
                                                    default: _withCtx(() => [
                                                        _createElementVNode("thead", null, [
                                                            _createElementVNode("tr", null, [
                                                                _createElementVNode("th", {
                                                                    class: "text-left cursor-pointer",
                                                                    style: { "user-select": "none" },
                                                                    onClick: _cache[4] || (_cache[4] = ($event) => (sortByColumn('Drug Name')))
                                                                }, [
                                                                    _createElementVNode("div", _hoisted_4, [
                                                                        _cache[10] || (_cache[10] = _createTextVNode(" Drug Name ", -1)),
                                                                        (sortColumn.value === 'Drug Name')
                                                                            ? (_openBlock(), _createBlock(_component_v_icon, {
                                                                                key: 0,
                                                                                icon: sortDirection.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down',
                                                                                size: "small",
                                                                                class: "ml-1"
                                                                            }, null, 8, ["icon"]))
                                                                            : _createCommentVNode("", true)
                                                                    ])
                                                                ]),
                                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleColumns.value, (field) => {
                                                                    return (_openBlock(), _createElementBlock("th", {
                                                                        key: field,
                                                                        class: "text-right cursor-pointer",
                                                                        style: { "user-select": "none" },
                                                                        onClick: ($event) => (sortByColumn(field))
                                                                    }, [
                                                                        _createElementVNode("div", _hoisted_6, [
                                                                            _createTextVNode(_toDisplayString(field) + " ", 1),
                                                                            (sortColumn.value === field)
                                                                                ? (_openBlock(), _createBlock(_component_v_icon, {
                                                                                    key: 0,
                                                                                    icon: sortDirection.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down',
                                                                                    size: "small",
                                                                                    class: "ml-1"
                                                                                }, null, 8, ["icon"]))
                                                                                : _createCommentVNode("", true)
                                                                        ])
                                                                    ], 8, _hoisted_5));
                                                                }), 128))
                                                            ])
                                                        ]),
                                                        _createElementVNode("tbody", null, [
                                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedData.value, (row, index) => {
                                                                return (_openBlock(), _createElementBlock("tr", { key: index }, [
                                                                    _createElementVNode("td", _hoisted_7, _toDisplayString(row.drugName || row["Drug Name"]), 1),
                                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleColumns.value, (field) => {
                                                                        return (_openBlock(), _createElementBlock("td", {
                                                                            key: field,
                                                                            class: _normalizeClass(["text-right", { 'text-error': isNegativeValue(row[getCamelCaseField(field)]) }])
                                                                        }, _toDisplayString(formatValue(row[getCamelCaseField(field)], field)), 3));
                                                                    }), 128))
                                                                ]));
                                                            }), 128))
                                                        ])
                                                    ]),
                                                    _: 1
                                                })
                                            ]),
                                            _: 1
                                        }))
                                        : _createCommentVNode("", true),
                        (formularyDetails.value && (formularyDetails.value.preferredDrugs || formularyDetails.value.comments))
                            ? (_openBlock(), _createBlock(_component_v_card_text, { key: 4 }, {
                                default: _withCtx(() => [
                                    _createVNode(_component_v_expansion_panels, { variant: "accordion" }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_expansion_panel, null, {
                                                default: _withCtx(() => [
                                                    _createVNode(_component_v_expansion_panel_title, null, {
                                                        default: _withCtx(() => _cache[11] || (_cache[11] = [
                                                            _createElementVNode("span", { class: "text-body-1 font-weight-medium" }, "View Formulary Details", -1)
                                                        ])),
                                                        _: 1,
                                                        __: [11]
                                                    }),
                                                    _createVNode(_component_v_expansion_panel_text, null, {
                                                        default: _withCtx(() => [
                                                            _createElementVNode("div", _hoisted_8, [
                                                                (formularyDetails.value.preferredDrugs)
                                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                                                        _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-body-2 font-weight-semibold mb-2" }, "Preferred drugs include:", -1)),
                                                                        _createElementVNode("div", _hoisted_10, _toDisplayString(formularyDetails.value.preferredDrugs), 1)
                                                                    ]))
                                                                    : _createCommentVNode("", true),
                                                                (formularyDetails.value.comments)
                                                                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                                                        _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-body-2 font-weight-semibold mb-2" }, "Comments:", -1)),
                                                                        _createElementVNode("div", _hoisted_12, _toDisplayString(formularyDetails.value.comments), 1)
                                                                    ]))
                                                                    : _createCommentVNode("", true)
                                                            ])
                                                        ]),
                                                        _: 1
                                                    })
                                                ]),
                                                _: 1
                                            })
                                        ]),
                                        _: 1
                                    })
                                ]),
                                _: 1
                            }))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }))
                : _createCommentVNode("", true);
        };
    }
});
